/**
 * Opening a second facility in its own window.
 *
 * PSI support and corporate users routinely need two facilities at once. The
 * app already supports that: the connection (`dietech_connection`) and the
 * facility (`dietech_facility`) both live in sessionStorage, which is scoped
 * per browsing context, and every request carries the connection as an
 * `X-Connection-Id` header and the facility as a URL segment. Two windows
 * therefore cannot contaminate each other — the server holds no "current
 * facility" of its own.
 *
 * What was missing is a way to GET the second window. In a browser tab that
 * was Ctrl+T; installed as a PWA (`display: standalone`) there is no tab strip
 * and no equivalent gesture, so the capability was effectively unreachable for
 * the people who need it most.
 *
 * Two rules make this work:
 *
 *  1. `noopener` is load-bearing, not hygiene. Without it the new window is an
 *     *auxiliary* browsing context and the spec hands it a COPY of the
 *     opener's sessionStorage — it would boot into the same facility, which is
 *     the exact trap this is meant to avoid. With `noopener` it is a fresh
 *     top-level context with empty storage, so `resolveStartRoute` sends it to
 *     the connection picker.
 *
 *  2. The target therefore has to travel in the URL, not in storage. It is a
 *     *request*, never an authorization: `ConnectionSelection` only honours the
 *     connection if it appears in the list the server returned for this user,
 *     and `TopToolbar` only honours the facility if it appears in that user's
 *     associated-facility list. A hand-crafted URL gets someone the connection
 *     picker, not another site's data.
 *
 * The opened URL stays inside the manifest `scope`, so an installed app opens
 * it as another app window rather than kicking the user out to a browser tab.
 */

import { isSafeReturnPath } from '../auth/returnAfterSignIn';

/** Where a handoff window starts. Always the picker — it validates. */
const HANDOFF_PATH = '/connection';

export const CONNECTION_PARAM = 'openConnection';
export const FACILITY_PARAM = 'openFacility';

/**
 * "Sign this window in." A `noopener` window starts with empty sessionStorage,
 * and oidc-client-ts keeps the signed-in user there (per tab, deliberately), so
 * the new window has no user and AuthGuard would show the public landing page
 * with its Sign In button. The person is already signed in to Keycloak in the
 * window that opened this one, so AuthGuard sees this flag and starts the
 * sign-in itself: Keycloak's session cookie answers it without a password and
 * the window lands on the organization picker.
 *
 * Only ever starts a normal sign-in, the same thing the Sign In button does,
 * so a hand-made link carrying it grants nothing.
 */
export const SIGNIN_PARAM = 'signin';


/**
 * Where the request lives between the URL and the two places that vet it.
 *
 * It cannot simply be read off `window.location` when it is needed, because a
 * `noopener` window has empty sessionStorage and oidc-client-ts keeps its user
 * there: the new window re-authenticates, and `onSigninCallback` in
 * `authConfig.ts` rewrites the URL to the bare pathname. The query string is
 * gone before `ConnectionSelection` ever renders. So the request is captured
 * at startup, ahead of any of that, and parked here — this window's own
 * storage, which survives the round trip.
 */
const HANDOFF_KEY = 'dietech_window_handoff';

/**
 * The page a handoff link asked for, parked beside the request. Tech Log's "Draft a menu with AI"
 * opens `/menucycle/builder?openConnection=..&openFacility=..&template=..&signin=1`: the new tab
 * signs in, the picker and the toolbar vet the connection and facility as for any handoff, and the
 * toolbar then opens this page, but only when the facility was honoured, so the page never opens on
 * a facility the link did not ask for. Like the ids it is a request, not a permission: the page's
 * own route guard still decides.
 */
const HANDOFF_PAGE_KEY = 'dietech_window_handoff_page';

export interface HandoffRequest {
  connectionId: number | null;
  facilityId: number | null;
}

const NO_HANDOFF: HandoffRequest = { connectionId: null, facilityId: null };

function isUsableId(value: number | null | undefined): value is number {
  return typeof value === 'number' && Number.isFinite(value) && value > 0;
}

/** The URL a handoff window opens at. Exported for testing. */
export function buildFacilityWindowUrl(target: HandoffRequest | Partial<HandoffRequest> = {}): string {
  const params = new URLSearchParams();
  if (isUsableId(target.connectionId)) params.set(CONNECTION_PARAM, String(target.connectionId));
  if (isUsableId(target.facilityId)) params.set(FACILITY_PARAM, String(target.facilityId));
  // Every handoff window is `noopener`, so every one of them needs signing in.
  params.set(SIGNIN_PARAM, '1');
  return `${HANDOFF_PATH}?${params.toString()}`;
}

/** Whether a location search string asks for this window to sign itself in. */
export function hasSigninRequest(search: string): boolean {
  try {
    return new URLSearchParams(search).get(SIGNIN_PARAM) === '1';
  } catch {
    return false;
  }
}

/**
 * Remove the sign-in flag from the address bar. Done before the sign-in leaves
 * for Keycloak, so Back, a refresh or a failed sign-in comes back to the plain
 * landing page instead of starting another sign-in (no redirect loop).
 */
export function clearSigninRequest(): void {
  try {
    const url = new URL(window.location.href);
    if (!url.searchParams.has(SIGNIN_PARAM)) return;
    url.searchParams.delete(SIGNIN_PARAM);
    window.history.replaceState(window.history.state, '', url.pathname + url.search + url.hash);
  } catch {
    // A URL we cannot rewrite is not worth failing startup over.
  }
}

/**
 * Open a facility in a second window (or a second app window when installed).
 *
 * With no target it opens the bare picker, which is the PSI-support case:
 * a different customer's organization entirely.
 */
export function openFacilityWindow(target: Partial<HandoffRequest> = {}): void {
  // `noopener` — see rule 1 in the file header. Do not "simplify" this away.
  window.open(buildFacilityWindowUrl(target), '_blank', 'noopener');
}

/** Read a handoff request out of a location search string. */
export function readHandoffTarget(search: string): HandoffRequest {
  let params: URLSearchParams;
  try {
    params = new URLSearchParams(search);
  } catch {
    return { ...NO_HANDOFF };
  }
  const connectionId = Number(params.get(CONNECTION_PARAM));
  const facilityId = Number(params.get(FACILITY_PARAM));
  return {
    connectionId: isUsableId(connectionId) ? connectionId : null,
    facilityId: isUsableId(facilityId) ? facilityId : null,
  };
}

/**
 * Called once at startup, before React renders and before auth can redirect.
 * Parks any request found in the URL and strips it, so a later reload or a
 * shared link does not re-fire a switch the user did not just ask for.
 */
export function captureHandoffFromUrl(): HandoffRequest {
  const target = readHandoffTarget(window.location.search);
  if (!target.connectionId && !target.facilityId) return { ...NO_HANDOFF };

  try {
    sessionStorage.setItem(HANDOFF_KEY, JSON.stringify(target));
  } catch {
    // Storage blocked (private mode, locked-down kiosk). The window still
    // opens; it just lands on the user's default facility.
  }

  try {
    const url = new URL(window.location.href);
    url.searchParams.delete(CONNECTION_PARAM);
    url.searchParams.delete(FACILITY_PARAM);
    window.history.replaceState({}, '', url.pathname + url.search + url.hash);
    parkHandoffPage(url);
  } catch {
    // A URL we cannot rewrite is not worth failing startup over.
  }

  return target;
}

/** Park the page a handoff link was made for (anything but the picker or the root). */
function parkHandoffPage(url: URL): void {
  const page = new URL(url.href);
  page.searchParams.delete(SIGNIN_PARAM);
  const path = page.pathname + page.search + page.hash;
  try {
    if (isSafeReturnPath(path)) sessionStorage.setItem(HANDOFF_PAGE_KEY, path);
    else sessionStorage.removeItem(HANDOFF_PAGE_KEY);
  } catch {
    // Storage blocked: the window lands on the start page, as a plain handoff does.
  }
}

/** The page the parked request was made for, if any. Read with the facility; cleared by clearHandoff. */
export function peekHandoffPage(): string | null {
  try {
    const path = sessionStorage.getItem(HANDOFF_PAGE_KEY);
    return isSafeReturnPath(path) ? path : null;
  } catch {
    return null;
  }
}

/** Read the parked request without consuming it. */
export function peekHandoff(): HandoffRequest {
  try {
    const raw = sessionStorage.getItem(HANDOFF_KEY);
    if (!raw) return { ...NO_HANDOFF };
    const parsed = JSON.parse(raw) as Partial<HandoffRequest>;
    return {
      connectionId: isUsableId(parsed?.connectionId) ? parsed.connectionId : null,
      facilityId: isUsableId(parsed?.facilityId) ? parsed.facilityId : null,
    };
  } catch {
    return { ...NO_HANDOFF };
  }
}

/**
 * Drop the parked request. Called once it has been honoured, and also when it
 * is refused — a request whose connection was rejected must not leave its
 * facility behind to be picked up against whatever the user chooses instead.
 */
export function clearHandoff(): void {
  try {
    sessionStorage.removeItem(HANDOFF_KEY);
    sessionStorage.removeItem(HANDOFF_PAGE_KEY);
  } catch {
    // Nothing to do; a request we cannot clear was one we could not store.
  }
}

/**
 * The security check, both halves of it: a requested id is honoured ONLY if it
 * appears in the list the server returned for this user — the allowed
 * connections from `/api/DietechConnection`, or the associated facilities from
 * the facility endpoint. A hand-crafted URL resolves to `null` here and the
 * caller falls back to the picker or the user's default facility.
 *
 * Kept as a plain function, apart from the components, so this rule can be
 * tested directly rather than through a rendered toolbar.
 */
export function resolveAllowed<T>(
  allowed: readonly T[] | null | undefined,
  requestedId: number | null | undefined,
  idOf: (item: T) => number,
): T | null {
  if (!isUsableId(requestedId) || !allowed) return null;
  return allowed.find((item) => idOf(item) === requestedId) ?? null;
}
